<template>

  <div>

    <el-form :inline="true" label-width="120px">

      <el-row :gutter="8">
        <el-col :span="12">

          <el-form-item v-if="isTowerCo()">
            Technical Proposal type: <strong>{{step2p2.state.tpType}}</strong>
          </el-form-item>

          <!-- XOL-534 (S5, D9): counterpartyTpLocked already folds in D8
               (locks once step 2 is Approved) and the honest-disabled arm.
               Bound to step2p2, never `step` — B1 found that RequestWorkflow
               can transiently repoint `step`/`stepData` at ANOTHER step's row
               during file-tab navigation; step2p2 is resolved once from
               rowData.steps at open and is immune to that race. -->
          <template v-else-if="step2p2">
            <select-tech-proposal v-model="step2p2.state.tpType" :disabled="counterpartyTpLocked" @change="fieldChanged(step2p2, 'state.tpType')">
              <template #cue><field-cue :row="step2p2" path="state.tpType" label="Technical Proposal type" layout="inline" /></template>
            </select-tech-proposal>
          </template>

          <!-- XOL-534 (B2): the note used to sit ABOVE the select, pushing
               "Tech. Proposal" ~35px lower than the Site Id column beside it.
               Both lines now sit below the select/input in both columns, so
               the two columns stay top-aligned. -->
          <div v-if="tpTypeNote"><small class="text-muted">{{tpTypeNote}}</small></div>
          <div v-if="tpTypeStamp"><small class="text-muted">{{lastUpdatedLine(tpTypeStamp)}}</small></div>

        </el-col>


        <el-col v-if="isTelco()" :span="12">
          <text-input label="Site Id" v-model="step2p2.state.siteId" :disabled="counterpartyTpLocked || !isRequestOwner()"
            @focus="holdField(step2p2, 'state.siteId')" @blur="releaseField(step2p2, 'state.siteId')" @input="fieldChanged(step2p2, 'state.siteId')">
            <template #cue><field-cue :row="step2p2" path="state.siteId" label="Telco Site ID" layout="inline" /></template>
          </text-input>
        </el-col>

        <el-col v-else :span="12">
          <el-form-item label="Telco Site ID">
            <b v-if="isTelco()">{{step.state.siteId}}</b>
            <b v-else>{{step2p2.state.siteId}}</b>
          </el-form-item>
        </el-col>

      </el-row>

      <el-row v-if="!isBackhaul" :gutter="8">
        <el-col :span="12">
          <select-service-type  v-model="step2p2.state.serviceType" :disabled="!canWriteStep() || (disabled && !isApproved) || frozen" @change="fieldChanged(step2p2, 'state.serviceType')">
            <template #cue><field-cue :row="step2p2" path="state.serviceType" label="Service Type" layout="inline" /></template>
          </select-service-type>
        </el-col>

        <el-col :span="12">
          <select-spectrum v-model="step2p2.state.spectrumBand" :disabled="!canWriteStep() || (disabled && !isApproved) || frozen" @change="fieldChanged(step2p2, 'state.spectrumBand')">
            <template #cue><field-cue :row="step2p2" path="state.spectrumBand" label="Spectrum Band" layout="inline" /></template>
          </select-spectrum>
        </el-col>
      </el-row>

</el-form>



<!-- XOL-526 (UF9): the approval readout is no longer Telco-only — a backhaul
     provider on an RTB sees the identical block. -->
<section v-if="!isTowerCo()">

  <div v-if="step2p1 && step2p1.approvedStatus && !isPending">
    <el-row>
      <el-col :span="24" v-if="step2p1.approvedStatus !== 'Pending'">
        <h2>{{step2p1.approvedStatus}}</h2>
        <p><i>{{step2p1.approvedStatus}} on {{formatDate(step2p1.approved_at)}}</i></p>
        <p v-if="step2p1.state.reSurvey || step2p1.state.reviseTP"><strong>Actions Required</strong></p>
        <p v-if="step2p1.state.reSurvey"><i class="fa fa-check"></i> Re-survey Required</p>
        <p v-if="step2p1.state.reviseTP"><i class="fa fa-check"></i> Revise Technical Propsal</p>

        <!-- XOL-596 (readable remarks): the shared framed block, hidden when there is no text
             (an emptied quill editor's `<p><br></p>` is not text). -->
        <read-only-remarks v-if="hasRemarksText(step2p1.remarks)" :label="step2p1.approvedStatus + ' Remarks'" :html="step2p1.remarks" />

      </el-col>
    </el-row>

  </div>

  <!-- RTD only: the removed trailing RTD editor showed a counterparty its own party-2 row's
       remarks here. Nobody can write that field on an RTD, so only a row that already carries
       text shows it, read-only (the same rule as `stepRemarksInForm`). -->
  <read-only-remarks v-if="actionData.actionType === 'RTD' && hasRemarksText(step.remarks)" label="Approval Remarks" :html="step.remarks" />

</section>

<section v-else>
  <!-- Carlos, 2026-10-15 ("step 2 remarks are duplicated"): this readout dates from 2021 and
       was the Approval Remarks summary for when the Approval panel is collapsed (the accordion
       closes it when Equipment opens). It always rendered, so with the panel open the same text
       showed twice. It now shows only while the panel is collapsed, or when the panel has no
       remarks row for this viewer (a view-only, non-approver user on an approved step), so
       that viewer still sees the remarks, once. -->
  <read-only-remarks v-if="showPane !== 'Approval' || !approvalRemarksRowShown" class="step2-remarks-summary" label="Approval Remarks" :html="step.remarks" />

  <el-form :inline="true">
    <el-collapse v-model="showPane" accordion>

    <!-- Approval - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <!-- XOL-596 (Carlos, 2026-10-14: "Move the lock to the top of the section, aligned to
         the right, just left of the toggle section arrow"): the lock line sits on the
         collapse's HEADER row, right-aligned, as a sibling of the item (positioned over
         its header), never inside the header's role="button". The wrapper is its
         positioning context; `--line` tells the narrow-width CSS to make room for it. -->
    <div v-if="isTowerCo()" class="approval-collapse" :class="{ 'approval-collapse--line': approvalLineShown(step) }">
    <!-- XOL-596 (approval lock): nothing locks by itself. The line says whether this
       approval is locked (and by whom), or offers to lock a decision. It comes BEFORE
       the item (the DOM order is the reading order of the wrapper: the line, then the
       header it is drawn over). -->
    <approval-lock-line
      ref="lockLine"
      placement="header"
      :row="step"
      :title="step.stepName"
      :decided="approvalDecided(step)"
      :frozen="frozen"
      :can-lock="canLockApproval(step) && !readOnly && !frozen"
      :can-unlock="canUnlockApproval(step) && !readOnly && !frozen"
      :busy="lockRunning !== null"
      :busy-label="lockBusyLabel()"
      :format="formatDateTime"
      @lock="lockApproval(step)"
      @unlock="unlockApproval(step)" />
    <el-collapse-item name="Approval">
      <template #title>
          <b>{{step.approvedStatus ? step.approvedStatus : 'Pending' }}</b>
          &nbsp;
          <!-- XOL-596 (decision fields, D7): the title shows the PICK, and the flag
               the API will set for it (displayApproved), so it never shows mixed icons
               while the pick waits for the pause or saves. "... ago" waits for the
               server (decisionConfirmed). -->
          <span v-if="!displayApproved(step)">Approval</span>
          &nbsp;
          <i v-if="displayApproved(step)" class="header-icon fa fa-check-circle text-success"></i>
          <i v-if="step.approvedStatus === 'Conditional'" class="header-icon fa fa-question-circle text-warning"></i>
          <i v-if="step.approvedStatus === 'Rejected'" class="header-icon fa fa-exclamation-circle text-danger"></i>
          <i v-if="!displayApproved(step) && (step.approvedStatus !== 'Rejected' && step.approvedStatus !== 'Conditional')" class="header-icon fa fa-info-circle text-muted"></i>
          &nbsp;
          <i v-if="displayApproved(step) && step.approvedStatus === 'Approved' && decisionConfirmed(step)" :title="approvalWhenTitle(step)">{{approvalWhen(step)}}</i>
      </template>

        <!-- XOL-596 (decision fields): the status and the date are FIELDS. A change
             marks them changed and restarts the pause; the C-12 card carries Pending,
             Saving and Saved. Nothing beside them, nothing disabled by a save. -->
        <div class="approval-row">
        <el-form-item class="approval-field" label="Approval">
          <select-approval-status
          v-model="step.approvedStatus"
          :disabled="!canDecideStep"
          @change="approval()">
            <template #cue><field-cue :row="step" path="approvedStatus" label="Approval Status" layout="inline" dirty /></template>
          </select-approval-status>

      </el-form-item>

      <date-input class="approval-field" label="Date" v-if="!isPending" v-model="step.approved_at" :format="dateFormat" :disabled="!canDecideStep" @change="approvalDate()">
        <template #cue><field-cue :row="step" path="approved_at" label="Approval Date" layout="inline" dirty /></template>
      </date-input>

      <check-box label="" v-if="!isPending" v-model="step.state.reviseTP"
        :disabled="!canDecideStep" @change="fieldChanged(step, 'state.reviseTP')">Revise {{step.state.tpType}}
        <template #cue><field-cue :row="step" path="state.reviseTP" label="Revise Technical Proposal" layout="inline" /></template>
      </check-box>

      <check-box label="" v-if="!isPending" v-model="step.state.reSurvey"
        :disabled="!canDecideStep" @change="fieldChanged(step, 'state.reSurvey')">Re-survey
        <template #cue><field-cue :row="step" path="state.reSurvey" label="Re-survey" layout="inline" /></template>
      </check-box>
        </div>


    <el-row v-if="approvalRemarksRowShown">

      <!-- XOL-596 (readable remarks): the heading is the same element in both states. It
           replaces the `type="primary"` "Approval Remarks" el-alert (an invalid type, so it
           rendered unstyled) shown when the request was not active, and the locked state's
           `<strong>`. -->
      <section class="remarks-section">
        <h5 class="remarks-heading">Approval Remarks</h5>

        <!-- XOL-608: the editor renders only when it would be enabled; for a viewer who cannot
             write it (view-only, read-only, no step) the block below replaces the disabled
             HtmlInput, which rendered nothing when empty and a fixed 111px box with text. -->
        <template v-if="!approvalRemarksReadOnly">
          <el-alert
            v-if="isActive(this.actionData)"
            type="warning"
            title="Please Note"
            description="These remarks will be visible to the sharer as well and will be sent in the approval e-mail."
            show-icon />

          <html-input v-model="step.remarks" cls-style="height: 34vh; width: 58.8vw; overflow: auto;" :disabled="disabled"
            @focus="holdField(step, 'remarks')" @blur="releaseField(step, 'remarks')" @input="fieldChanged(step, 'remarks')" />
          <field-cue :row="step" path="remarks" label="Approval Remarks" layout="block" />
        </template>

        <!-- Locked, complete (XOL-596) or not writable by this viewer (XOL-608): the remarks stay
             visible and read-only, with an explicit empty state (a disabled, empty HtmlInput
             renders nothing at all). "Please Note" goes with the editor. -->
        <read-only-remarks v-else aria-label="Approval Remarks" :html="step.remarks" />
      </section>
    </el-row>


    <el-row v-if="isApprover() && canAccess('project')" align="right">
      <el-form-item>
        <!-- XOL-586 (D-2026-10-16c, Q13): "Send {status} e-Mail" until this content has been
             e-mailed, then "Re-send {status} e-Mail" (it asks first), with the last-sent line
             under it. The server says which: `step.approvalMail`. -->
        <!-- Delta 1 (item 1): disabled while Send E-mails is off. A disabled button fires no mouse
             events, so the tooltip sits on the wrapper (the UtilityReport pattern); the request
             banner is the persistent explanation. -->
        <el-tooltip :disabled="!requestMailOff()" :tabindex="requestMailOff() ? 0 : -1" :content="approvalMailOffTitle()" placement="top" effect="dark">
          <span class="approval-send-wrap" :class="{ 'is-mail-off': requestMailOff() }" :tabindex="requestMailOff() ? 0 : -1">
            <el-button
              :type="approvalButton"
              :loading="approvalMailSending"
              @click="sendApproval()"
              :disabled="!canWriteStep() || !step.approvedStatus || approvalMailSending || requestMailOff()">{{approvalSendLabel(step)}}</el-button>
          </span>
        </el-tooltip>
        <approval-mail-line :step="step" :action-data="actionData" />
        <!-- Delta 1 (item 4): a Re-send opens this picker instead of a confirm; the first Send never does. -->
        <resend-recipients-dialog v-bind="resendDialogProps()"
          @send="submitResend" @retry="retryResendPreview" @close="closeResendPicker" />
      </el-form-item>

      <el-form-item v-if="isRejected">
        <check-box style="background-color: #faedd9" border v-model="closeTick"
          :disabled="!canClosePermanently || rejecting"
          @change="setRejected">Close this Request permanently?</check-box>
        <cmd-saving :busy="rejecting" :label="rejectingLabel" />
      </el-form-item>

    </el-row>
</el-collapse-item>
    </div>


  <!-- RTS ONLY: Equipment Input - - - - - - - - - - - - - - - - - - - - - - - - - -->
  <el-collapse-item name="Telco Equipment" v-if="!isViewOnly() && (!step.approved && step.actionType==='RTS' && isActive(this.actionData))">
    <template #title>
        Allow Telco to Add Equipment&nbsp;
        <i v-if="step.state.allowClientEquipmentInput" class="header-icon fa fa-check-circle text-primary"></i>
        <i v-else class="header-icon fa fa-minus-circle text-warning"></i>
    </template>
    <el-form-item>
      <check-box v-model="step.state.allowClientEquipmentInput" :disabled="fieldsDisabled" @change="fieldChanged(step, 'state.allowClientEquipmentInput')">Allow Telcos to Input Equipment?
        <template #cue><field-cue :row="step" path="state.allowClientEquipmentInput" label="Allow Telcos to Input Equipment" layout="inline" /></template>
      </check-box>
    </el-form-item>
  </el-collapse-item>

</el-collapse>
</el-form>

</section>


<!-- RTS ONLY: Equipment Form.
     XOL-596 (R2.20): the telco's gate is `canTelcoEditEquipment`. It used to read
     `step.state.allowClientEquipmentInput` and `step.approved`, but for a telco
     `step` is its OWN party-2 row (step2p2), which never carries the flag the Tower
     Co ticks on party 1 and is never approved: the form was unreachable. -->
<section v-if="(!isViewOnly() && isTowerCo() && (actionData.status != 'Closed' && actionData.status != 'Rejected')) || canTelcoEditEquipment">

  <el-form v-if="equipFormShown" label-position="top" v-model="item">

    <el-row :gutter="8" v-if="isTowerCo() || canTelcoEditEquipment">
      <el-col :xs="24" :sm="24" :md="12" :lg="7" style="padding-top: 0.28em;"> <!-- :show-levels="false" -->
        <select-equip-category label="Type" v-model="catType"  size="small" :disabled="equipDisabled" @change="nameCat" />
      </el-col>

      <el-col :xs="23" :sm="23" :md="11" :lg="4">
        <number-input prop="qty" label="Qty" v-model="item.qty" size="small" :min="1" :disabled="equipDisabled" />
      </el-col>

      <el-col :xs="23" :sm="23" :md="11" :lg="4">
        <number-input prop="setNo" label="Set #" v-model="item.setNo" size="small" :min="1" :disabled="equipDisabled" />
      </el-col>

      <el-col :xs="23" :sm="23" :md="11" :lg="4">
        <float-input prop="size" label="Dia." v-model="item.size" size="small" suffix="⌀ mm" :min="1" :disabled="equipDisabled" />
      </el-col>

      <el-col :xs="23" :sm="23" :md="11" :lg="4">
        <float-input prop="agl" label="Height" v-model="item.agl" size="small" suffix="AGL" :min="0" :disabled="equipDisabled" />
      </el-col>

      <el-col :xs="24" :sm="12" :md="6" :lg="isTelco() ? 3 : 4">
        <el-form-item v-if="isTowerCo()" prop="billable" :label="'Billable ' + money">
          <el-switch active-text="Yes" inactive-text="No" v-model="item.billable" size="small" :disabled="equipDisabled" />
        </el-input>
      </el-form-item>
    </el-col>

    <el-col v-if="item.billable" :xs="23" :sm="11" :md="5" :lg="5" :offset="1">
      <float-input prop="amount" label="Amount" v-model="item.amount" size="small" :prefix="money" :min="0" :disabled="equipDisabled" />
    </el-col>

    <el-col v-if="item.billable"  :xs="23" :sm="11" :md="6" :lg="5" :offset="1">
      <select-frequency size="small" v-model="item.frequency" :disabled="equipDisabled" />
    </el-col>

    <el-col v-if="canEditEquipRows" align="right" :xs="24" :sm="12" :md="2" :lg="2">
      <el-form-item  style="padding-top: 2.5em;">
        <el-button
          type="success"
          :size="this.isDesktop ? 'large' : 'small'"
          icon="fa fa-plus"
          @click="addRow()"
          :disabled="!item.name && !item.quantity > 0">
          {{isDesktop? 'Add' : ''}}
        </el-button>
      </el-form-item>
    </el-col>
  </el-row>

</el-form>

</section>

<!-- RTS ONLY -->
<section v-if="equipment && equipment.length > 0">
  <br />
  <h4>Equipment</h4>
  <div style="display: block;">
    <el-table
    :data="equipment"
    show-summary
    sum-text="Totals"
     border style="width: 100%"
     stripe
     max-height="300">

<!--
    <el-table-column label="Type">
          <template #default="scope">
            <span v-if="equipment[scope.$index].typeName">
              {{equipment[scope.$index].type[equipment[scope.$index].type.length-1]}}
            </span>
          <i v-if="equipment[scope.$index].addedByClient" class="fa fa-user text-muted">&nbsp;</i>
          </template>
    </el-table-column>
-->
    <el-table-column prop="name" label="Type" :width="this.isDesktop ? 396 : 220" align="left" />
    <el-table-column prop="qty" label="Qty" align="right" />
    <el-table-column class="hidden-md-and-down" prop="setNo" label="Set #" align="right" />
    <el-table-column class="hidden-md-and-down" prop="size" label="Size" align="right" />
    <el-table-column class="hidden-md-and-down" prop="agl" label="AGL" align="right" />

    <el-table-column class="hidden-sm-and-down" prop="billable" label="Billable">
      <template #default="scope">
        <b>{{equipment[scope.$index].billable ? 'Y' : 'N'}}</b>
      </template>
    </el-table-column>

    <el-table-column v-if="isTowerCo()" class="hidden-sm-and-down" prop="amount" :label="money">
      <template #default="scope">
        <span v-if="equipment[scope.$index].amount">{{formatNumber(equipment[scope.$index].amount)}}</span>
        <i v-if="equipment[scope.$index].frequency" class="text-muted"><small>{{equipment[scope.$index].frequency}}</small>&nbsp;</i>
      </template>
    </el-table-column>

    <el-table-column label="="
      v-if="canEditEquipRows"
      align="center">
      <template #default="scope">
        <el-button v-if="isTowerCo() ? !step.approved : equipment[scope.$index].addedByClient"
        @click.native.prevent="deleteRow(scope.$index, equipment)"
        type="danger" icon="fa fa-trash-alt" size="mini" circle>
      </el-button>
    </template>
  </el-table-column>
</el-table>
<!-- XOL-596 (UX item 16): the telco may remove only the rows its organisation added. -->
<small v-if="canTelcoEditEquipment" class="text-muted equipment-own-rows">{{ownRowsNote}}</small>
</div>
</section>

<!-- XOL-526 (C3): equip-table fetches /assets/site/{siteId}/owner/{organisation_id}/…,
     a Tower-Co cross-tenant read (tenant.towerco). A provider viewer used to hit that
     unguarded and 403. Hidden silently for anyone who can't read that org (UF9). -->
<section v-if="actionData.actionType !== 'RTS' && canReadOrgScoped(actionData.organisation_id)">
  <equip-table
    :title="(isBackhaul ? 'Backhaul' : actionData.organisation.shortName) + ' Equipment'"
    :site-id="actionData.site_id"
    :owner-id="actionData.organisation_id"
    :site-data="actionData.site"
    :no-add="!canWriteStep() || frozen"
    :step-frozen="frozen"
    :action-step-id="step2ActionStepId"
    :filter-col="isBackhaul ? 'type' : null"
    :filter-val="isBackhaul ? 'backhaul' : null" />
</section>

<!-- Carlos, 2026-10-15: the RTD-only "Approval Remarks" editor that sat here edited the same
     `step.remarks` as the Approval panel above. Before KAN-29 (2026-04) its condition compared
     against an undefined `RTD` identifier and never rendered; KAN-29 quoted it and switched on a
     duplicate editor. Removed: an RTD's approval remarks live in the Approval panel like every type. -->

</div>
</template>



<script>

import {settings} from '@/components/Data'

import DateMixins from '@/mixins/DateMixins'
import RequestMixins from '@/mixins/RequestMixins'

import FileDownload from '@/components/atoms/files/FileDownload'
import FileUpload from '@/components/atoms/files/FileUpload'

import SelectApprovalStatus from '@/components/atoms/selects/SelectApprovalStatus'
import SelectEquipCategory from '@/components/atoms/selects/SelectEquipCategory'
import SelectFrequency from '@/components/atoms/selects/SelectFrequency'

import SelectSpectrum from '@/components/atoms/selects/SelectSpectrum'
import SelectServiceType from '@/components/atoms/selects/SelectServiceType'

import SelectTechProposal from '@/components/atoms/selects/SelectTechProposal'

import EquipTable from '@/components/assets/EquipTable'
import FieldCue from '@/components/requests/FieldCue'
import CmdSaving from '@/components/requests/CmdSaving'
import ApprovalLockLine from '@/components/requests/ApprovalLockLine'
import ApprovalMailLine from '@/components/requests/ApprovalMailLine'
import ResendRecipientsDialog from '@/components/requests/ResendRecipientsDialog'
import ReadOnlyRemarks from '@/components/atoms/ReadOnlyRemarks'

import { copyText } from '@/lib/requestSyncCopy'
import * as commandNotices from '@/lib/commandNotices'
import * as savePopup from '@/lib/savePopup'
import { activityText, escapeHtml } from '@/lib/requestActivity'

export default {
  components: {
    ApprovalLockLine,
    ApprovalMailLine,
    ResendRecipientsDialog,
    CmdSaving,
    EquipTable,
    FieldCue,
    FileDownload,
    FileUpload,
    ReadOnlyRemarks,
    SelectApprovalStatus,
    SelectEquipCategory,
    SelectFrequency,
    SelectSpectrum,
    SelectServiceType,
    SelectTechProposal
  },

  mixins: [DateMixins, RequestMixins],

  props: {

    rowData: {
      type: Object,
      required: true
    },

    rowIndex: {
      type: Number,
      default: null
    },

    stepData: {
      type: Object,
      default: null
    },

    readOnly: {
      type: Boolean,
      default: true
    }

  },



  methods: {

    // XOL-596 (decision fields, Carlos 2026-10-14): the approval status and date are
    // FIELDS, like every other: a change marks the field changed and restarts the
    // form's 3 s pause, and at the pause the group goes out ONCE, as one approval
    // command with `expectedApprovedStatus` = the status the user SAW (the field's
    // base, never the v-model, which has already changed). The C-12 card shows
    // Pending, Saving and Saved; nothing here disables or hides anything. Nothing is
    // stamped locally (approved, approved_by, the Re-survey / Revise TP clearing): the
    // server decides and the adopt of its response shows the result (XOL-374's decision
    // date is the server's too). A refusal puts the server's decision on screen.
    approval () {
      this.ensureState(this.step)

      return this.decisionChanged(this.step, 'approvedStatus')
    },

    // The approver back-dated the decision (XOL-374 kept the Date editable). Clearing
    // the picker (the ×) puts the date back and records nothing; a pending status pick
    // in the same pause is kept.
    approvalDate () {
      if (!this.step.approved_at) {
        return this.decisionDateCleared(this.step)
      }

      return this.decisionChanged(this.step, 'approved_at')
    },



    // XOL-584 (Carlos, 2026-10-05): "Close this Request permanently?" finalises
    // IMMEDIATELY again, as before Feb 2026 (baseline 7addf580): on confirm the
    // request is Rejected and the shared actionData swaps the drawer to the
    // read-only CompletedRequest. Kept from the XOL-582 fixes: the two-callback
    // .then(ok, cancel), no $refs.rtsTable call, and Cancel unticks the box.
    //
    // XOL-596 (slice 3): the confirm closes on Reject and the command runs OUTSIDE
    // it, with "Rejecting…" shown inline next to the box. It cannot run inside the
    // confirm: Element's MessageBox is a single instance with a queue, so the hang
    // dialog (a save or the POST that never answers: Keep waiting / Cancel) would
    // queue behind the very box that is waiting and never show. The remarks are
    // saved first and the command is NOT sent if that save fails (the ordering
    // rule, XOL-581/585); a failure unticks the box.
    setRejected (value) {

      // Untick: moot. A confirmed reject unmounts this step (the drawer swaps
      // to CompletedRequest), and Cancel has already reset the box.
      if (!value) return

      var self = this

      var rtx = this.actionData.actionType

      this.$confirm(this.rejectConfirmMessage(), copyText('requestSync.reject.confirm.title', { rtx: rtx }), {
        confirmButtonText: copyText('requestSync.reject.confirm.ok'),
        // Red on purpose (Carlos, 2026-10-18): closing a request for good is
        // a one-off, final act, so the box reads like an error, not a warning.
        confirmButtonClass: 'el-button--danger',
        cancelButtonText: copyText('requestSync.reject.confirm.cancel'),
        center: true,
        dangerouslyUseHTMLString: true,
        type: 'error'
      }).then(function () {
        self.rejecting = true

        var finish = function (ok) {
          self.rejecting = false

          if (!ok) {
            self.closeTick = false
          }
        }

        // Whatever happens, "Rejecting…" ends and the box is released: a throw
        // here (or a rejected command) must not lock the control.
        var run
        try {
          run = self.rejectRequest()
        } catch (e) {
          run = Promise.reject(e)
        }

        return run.then(function (out) {
          finish(out.ok)
        }, function (e) {
          console.error('XOL-596: rejecting the request threw', e)
          finish(false)
        })
      }, function () {
        // The check-box has already flipped itself; pushing the display flag
        // back to false is what makes the atom (watching `value`) untick.
        self.closeTick = false
      })
    },

    // The body of the "Close this {RTx} permanently?" confirm. Delta 1 (item 5): it says what
    // closing means (the status, that no one can change it, that it can't be undone). The copy
    // keys are plain text; every one is escaped here because the confirm renders HTML.
    // XOL-586 (N7): closing the request unmounts this step, and the Rejected e-mail (which is
    // what announces the closure, Q2) can no longer be sent from here. So while it has not been
    // sent (the party-1 row's `approvalMail` is null), the confirm says so. Left out while Send
    // E-mails is off (nothing could be sent anyway, U5), and when the key is missing.
    rejectConfirmMessage () {
      var rtx = this.actionData.actionType
      var html = escapeHtml(copyText('requestSync.reject.confirm.body', { rtx: rtx })) +
        '<br><br><b>' + escapeHtml(copyText('requestSync.closing.final')) + '</b>'
      var p1 = this.step2p1 || this.step

      if (!p1 || p1.approvalMail !== null || this.requestMailOff()) {
        return html
      }

      var org = this.actionData.organisation
      var owner = org ? (org.name || org.shortName) : 'the owner'

      return html + '<br><br>' + escapeHtml(activityText('requestMail.reject.notSent', { owner: owner, rtx: rtx }))
    },

    // POST /actions/{id}/reject {stepId}. Queued behind the approval(Rejected)
    // command on the step row (the select change sent it), after the remarks are
    // flushed. The drawer swaps when the response's `status: 'Rejected'` is adopted.
    rejectRequest () {
      var self = this
      var p1 = this.step2p1 || this.step
      var action = this.actionData

      return this.workflowCommand({
        kind: 'reject',
        step: p1,
        step2p1: p1,
        rows: [p1, action],
        url: '/actions/' + action.id + '/reject',
        body: { stepId: p1.id },
        label: 'Reject',
        requireSaved: true,
        ordering: { kind: 'reject' },
        verify: function (fresh) {
          return fresh.status === 'Rejected'
        },
        revert: function () {
          self.closeTick = false
        },
        onOk: function () {
          savePopup.success(commandNotices.doneMessage('reject', { rtx: action.actionType, action: action }).message)
          self.refreshStats(self.org().id, true)
        }
      })
    },



    ensureState (s) {
      if (typeof s === 'object' && s !== null && (s.state === null || s.state === undefined)) {
        this.$set(s, 'state', {})
      }
      return s
    },



    // XOL-596: the fresh GETs on open are adopted IN PLACE into the one object
    // for each row (F4), with the seq of when they were issued, never swapped in.
    // A late response therefore cannot revert an approval (B7), and a field the
    // user is editing keeps its text (R2.7).
    loadOwnStep () {
      this.loadStep(this.stepData.id, this.step, this.ensureState)
    },

    // XOL-534 (B1 defense-in-depth): the counterparty's TP type / Site Id gate,
    // template binding and save all read step2p2 — never `step`/`stepData`, which
    // RequestWorkflow can transiently repoint at another step's row. Refreshing
    // on open therefore targets step2p2's OWN id and merges the fresh row into
    // step2p2.
    loadStep2p2 () {
      this.loadStep(this.step2p2.id, this.step2p2, this.ensureState)
    },



    nameCat (data) {

      this.item.category = data[0] ? data[0] : null
      this.item.type = data[1] ? data[1] : null
      this.item.subType = data[2] ? data[2] : null
      this.item.variant = data[3] ? data[3] : null

      this.item.name = this.item.category + (this.item.type ? ' / ' + this.item.type : '')
      this.item.name += (this.item.subType ? ' / ' + this.item.subType : '')
      this.item.name += (this.item.variant ? ' / ' + this.item.variant : '')
    },



    addRow () {
      // A completed step takes no equipment (XOL-596): the form is not rendered; this is the
      // handler's own guard.
      if (this.frozen) {
        return
      }

      if (!this.equipment) {
        this.equipment = []
      }

      var added = this.clone(this.item)

      // The server stamps `addedByClient` and is the authority (R2.20); this is for
      // the display until the response is adopted. Read when the row is added, not
      // when the component was created (the store may not have been ready then).
      added.addedByClient = this.isTelco()

      this.equipment.push(added)

      this.item.category = null
      this.item.type = null
      this.item.subType = null
      this.item.variant = null
      this.item.name = null
      this.item.qty = 1
      this.item.agl = 0
      this.item.size = 0
      this.setNo = 0
      this.item.billable = false
      this.item.amount = null
      this.item.frequency = null
      this.item.addedByClient = this.isTelco()

      // XOL-596: marks the list changed; it is saved whole at the form pause.
      this.fieldChanged(this.actionData, 'state.step2.equipment')
    },


    deleteRow (index, rows) {
      if (this.frozen) {
        return
      }

      rows.splice(index, 1)
      this.fieldChanged(this.actionData, 'state.step2.equipment')
    },


    // The click of "Send / Re-send {status} e-Mail". RequestMixins.sendApprovalEmail runs
    // it: XOL-582 / XOL-596 ordering rule: the changed fields are flushed (the form pause
    // cut short) before the e-mail, so what the recipients read is what the app holds, and
    // nothing is sent if one of those saves failed (its own notice says so). The first Send
    // then goes to everyone; a Re-send opens the recipient picker (Delta 1, item 4).
    sendApproval () {
      this.ensureState(this.step)

      return this.sendApprovalEmail(this.step)
    },

    // XOL-586 (F2): the API sends the e-mail (to the owner, from the saved step). Nothing
    // about the recipients or the body leaves the browser: just the step.
    sendApprovalMail () {
      return this.notifyRequest('approval_notice', { stepId: this.step.id })
    },

  },



  watch: {

    // The editors unmount when the step freezes, and an unmounting quill gets no blur: let go
    // of the text paths they hold, so nothing stays held by focus.
    frozen (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
        // The telco's Site Id (an input that is disabled in place, on step2p2's own row).
        if (this.step2p2) {
          this.releaseField(this.step2p2, 'state.siteId')
        }
      }
    },

    // The Approval Remarks editor unmounts when the approval locks (an adopt), and an
    // unmounting quill gets no blur: let go of the path it holds (F-L1, 2026-10-15).
    locked (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
      }
    },

    // XOL-608: the same, when the viewer stops being able to write it (the request closes, a
    // view-only flag arrives): the editor unmounts and a quill gets no blur.
    approvalRemarksReadOnly (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
      }
    }

  },


  data () {

    return {

      money: settings.currency,
      dateFormat: settings.selectDateFormat,

      item: {category: null, type: null, subType: null, variant: null, name: null, qty: 1, size: 0, agl: 0, billable: false, amount: null, frequency: null, addedByClient: this.isTelco()},

      actionData: this.rowData,

      backhaul: this.rowData && this.rowData.backhaul ? this.rowData.backhaul : null,

      step: this.stepData,

      // XOL-584: what the "Close this Request permanently?" box DISPLAYS. The
      // box is v-modelled to it (not to anything on the step) so Cancel can
      // untick it: the CheckBox atom keeps its own `form` copy, which follows
      // `value` only when the prop actually changes. Nothing is persisted from
      // it; a confirmed reject finalises the request instead (setRejected).
      closeTick: false,

      // The close-permanently command is running (the inline "Rejecting…").
      rejecting: false,
      rejectingLabel: copyText('requestSync.reject.busy'),

      catType: [],

      step2p1: null,
      step2p2: null,
      isApproved: false,

      showPane: this.isTelco() ? 'Equipment' : 'Approval',

      equipment: this.rowData.state.step2.equipment,

      ownRowsNote: copyText('requestSync.equipment.ownRows')

    }

  }, // data


  computed: {

    // The approver's gate (unchanged): the approval controls and the handlers
    // that back them.
    // XOL-596 (approval lock): a LOCKED approval is read-only until it is unlocked.
    // XOL-596 (completed steps): a COMPLETED step is read-only too (`frozen`).
    canDecideStep () {
      return !this.disabled && this.isApprover() && this.canAccess('project') && !this.locked && !this.frozen
    },

    // XOL-596 (completed steps, Carlos 2026-10-14): this step is complete, so everything in
    // it is read-only for every viewer until it is re-opened. A CONSTANT step number (never
    // `step.stepNo`: RequestWorkflow can transiently repoint `step`), from the server baseline.
    // NOT folded into `disabled`: Close this Request permanently reads `disabled` and must stay
    // enabled (CS-1 contrast).
    frozen () {
      return this.isStepFrozen(2)
    },

    // The controls that read `disabled` and also freeze.
    fieldsDisabled () {
      return this.disabled || this.frozen
    },

    // The id of this request's step-2 party-1 row: the step context the equipment table sends
    // (`actionStepId`) so the API can refuse an equipment write while step 2 is complete.
    step2ActionStepId () {
      var row = this.partyOneStepRow(2)

      return row ? row.id : null
    },

    // Close this Request permanently? is the reject command, which the lock does not
    // touch: its gate is the approver's, without the lock.
    canClosePermanently () {
      return !this.disabled && this.isApprover() && this.canAccess('project')
    },

    // Does the Approval panel carry the remarks row for this viewer? Hidden for a view-only
    // user who is not a project approver once the step is approved.
    approvalRemarksRowShown () {
      return !this.step.approved || !this.isViewOnly() || (this.isApprover() && this.canAccess('project'))
    },

    // Is this step's approval locked? (An owned key of the row: reactive.)
    locked () {
      return this.approvalLocked(this.step)
    },

    // XOL-608: the Approval Remarks block shows exactly when the editor's own `:disabled`
    // (`disabled`) would be true, OR'd with the locked / complete terms (XOL-596).
    approvalRemarksReadOnly () {
      return this.locked || this.frozen || this.disabled
    },

    disabled () {
      // XOL-584: no live Closed/Rejected clause. A request that was terminal at
      // mount is already locked through `readOnly` (RequestWorkflow.readOnly /
      // CompletedRequest's default), which matches the pre-XOL-348 behaviour.
      //
      // XOL-596 (Carlos, 2026-10-13): approving no longer locks anything by itself
      // ("it does not give us time to change dates or fix anything"). The old
      // `approved && approvedStatus === 'Approved'` clause is gone, live and as a
      // snapshot; an approval is locked only by the lock command (`locked`).
      return !this.canWriteStep() || this.isViewOnly() || this.readOnly || !this.stepData
    },

    // XOL-596 (R2.20): may the REQUEST OWNER (a telco) add and remove its own
    // equipment rows here? RTS only; the Tower Co ticked "Allow Telcos to Input
    // Equipment" on step 2 PARTY 1; that step is neither completed nor approved; the
    // viewer is not view-only and the request is open. The flag, `approved` and
    // `completed` are read from the party-1 row (RequestMixins.canTelcoWriteEquipment
    // reads it from actionData.steps), never from `step`, which for a telco is its own
    // party-2 row (step2p2).
    canTelcoEditEquipment () {
      return !this.isTowerCo() && !this.readOnly && this.canTelcoWriteEquipment()
    },

    // The equipment form (Type, Qty, Set #, Dia., Height, Add). The Tower Co's rule
    // is unchanged; the telco's is `canTelcoEditEquipment`.
    equipFormShown () {
      if (this.isTowerCo()) {
        return !this.isViewOnly() && this.step.actionType === 'RTS' && !this.step.approved && !this.frozen
      }

      return this.canTelcoEditEquipment
    },

    // The equipment inputs. `disabled` is `!canWriteStep() || ...`, which is true for
    // EVERY telco, so the telco has its own rule.
    equipDisabled () {
      return this.isTowerCo() ? this.disabled : !this.canTelcoEditEquipment
    },

    // Add and the delete column.
    canEditEquipRows () {
      return this.isTowerCo() ? (!this.disabled && !this.step.approved && !this.frozen) : this.canTelcoEditEquipment
    },

    // XOL-526/534 (D8, D5): TP type / Site Id lock once the Tower Co approves
    // step 2. Gated on step2p2 (B1: never on `step`/`stepData`, which
    // RequestWorkflow can transiently repoint at another step's row).
    counterpartyTpLocked () {
      return !this.canWriteOwnStep(this.step2p2) || this.readOnly || this.isApproved
    },

    // XOL-526 (UX §2.4, U-I): the TP type stamp lives on the party-2 row,
    // step2p2, for every viewer (Tower Co reads it, the owner/provider write
    // it) — unlike Progress Remarks, this field IS shown to the Tower Co too.
    tpTypeStamp () {
      return (this.step2p2 && this.step2p2.state) ? this.editStamp(this.step2p2.state, 'tpType') : null
    },

    tpTypeNote () {
      return (!this.counterpartyTpLocked && this.hasDistinctProvider())
        ? this.sharedFieldNote('change the Technical Proposal type')
        : null
    }

  },



  created () {

    if (this.step) {
      this.ensureState(this.step)

      if (this.canWriteStep() && !this.readOnly) {
        this.loadOwnStep()
      }

    this.step2p1 = this.findWorkflowStep(2, 1, this.rowData.steps)
    this.ensureState(this.step2p1)
    this.step2p2 = this.findWorkflowStep(2, 2, this.rowData.steps)
    this.ensureState(this.step2p2)

    if (this.step && this.step.stepNo === 2 && this.step.partyNo === 1) {
        this.step2p1 = this.step
    }


    if (this.step && this.step.stepNo === 2 && this.step.partyNo === 2) {
        this.step = this.step2p2
    }


    if (this.step2p1) {
       this.isApproved = (this.step2p1.approvedStatus === 'Approved')
    }

      // XOL-534 (B1): a SEPARATE counterparty fetch, always targeting
      // step2p2's own id — never `stepData.id`, which is the prop
      // RequestWorkflow can transiently repoint at another step's row.
      // Round-3 fix: `!this.isTowerCo() &&` is load-bearing — canWriteOwnStep()
      // is `isTowerCo() ? canWriteStep() : …`, so without it a writable Tower
      // Co ALSO triggered this fetch (a redundant second GET whose async
      // setStep2p2() could then replace step2p2 out from under an in-flight
      // edit to Service Type / Spectrum, which bind step2p2.state directly).
      if (!this.isTowerCo() && this.canWriteOwnStep(this.step2p2) && !this.readOnly && this.step2p2) {
        this.loadStep2p2()
      }

      // Fix existing data: RTS mixins
      this.fixExistingEquipment()

    }

  },



  beforeDestroy () {

    // XOL-596: no save here (it sent whole rows, workflow state included, from a
    // possibly stale drawer, which is the incident). The content fields of this
    // step save at the form's pause or at a flush point; a text field still
    // focused gets no blur on unmount.
    this.releaseFocused()

    if (!this.readOnly && this.step) {
      this.$emit('updated', this.step)
    }

  }

}
</script>


<style scoped>
  .el-table__header {
    table-layout: auto !important;
  }

  /* XOL-593: in the approval row the italic labels ("Approval", "Approval
     Status", "Date") sat 3.2px below everything beside them: the select's
     text, the read-only Date value and the Revise / Re-survey checkboxes. The
     global .el-form-item__label rule in src/assets/css/app.css adds
     padding-top: 3.2px to every label. Drop it for this row only, so labels,
     values and checkboxes share one text line in both the read-only and the
     editable state. */
  .approval-field ::v-deep .el-form-item__label {
    padding-top: 0;
  }

  /* XOL-596 (Carlos, 2026-10-14): the approval row sits directly above the remarks. Its
     form items reserve Element's 22px of validation space under each (nested ones twice),
     which left a blank band where the lock line used to be. */
  .approval-row ::v-deep .el-form-item {
    margin-bottom: 0;
  }

  /* app.css makes every .el-select a BLOCK, so the 1.5em cue slot after the status select
     wrapped to a second line and doubled the row's height (the blank band under the
     approval row, where the C-7 used to wrap). Inline, at the width it always rendered at,
     the slot stays on the select's line. */
  .approval-field ::v-deep .el-select {
    display: inline-block;
    width: 192px;
    vertical-align: top;
  }

  .approval-row {
    margin-bottom: 10px;
  }

  /* Delta 1 (item 1): the tooltip wrapper of the Send / Re-send button. */
  .approval-send-wrap {
    display: inline-block;
  }
  .approval-send-wrap.is-mail-off,
  .approval-send-wrap.is-mail-off ::v-deep .el-button {
    cursor: not-allowed;
  }
</style>
